import type { Unit } from '@/api/pending'
import { Chip, Eyebrow } from '@/ui'
import styles from './PublishModal.module.css'
import {
  audienceLabel,
  floorsOf,
  towerNames,
  type AudienceMode,
  type AudienceSelection,
} from './audience'

const MODES: { id: AudienceMode; label: string }[] = [
  { id: 'all', label: 'Condomínio inteiro' },
  { id: 'towers', label: 'Torres' },
  { id: 'floors', label: 'Andares' },
  { id: 'units', label: 'Unidades' },
]

const toggle = <T,>(list: T[], v: T): T[] =>
  list.includes(v) ? list.filter((x) => x !== v) : [...list, v]

interface Props {
  units: Unit[]
  value: AudienceSelection
  onChange: (next: AudienceSelection) => void
}

/** Who sees it: whole condominium, towers, floors or hand-picked units. Resolves to unit ids. */
export function AudiencePicker({ units, value, onChange }: Props) {
  return (
    <div className={styles.group}>
      <Eyebrow>Quem vê</Eyebrow>
      <div className={styles.chips} role="group" aria-label="Público">
        {MODES.filter((m) => m.id !== 'floors' || floorsOf(units).length > 0).map((m) => (
          <Chip
            key={m.id}
            selected={value.mode === m.id}
            onClick={() => onChange({ ...value, mode: m.id })}
          >
            {m.label}
          </Chip>
        ))}
      </div>

      {value.mode === 'towers' && (
        <div className={styles.chips} role="group" aria-label="Torres">
          {towerNames(units).map((t) => (
            <Chip
              key={t}
              selected={value.towers.includes(t)}
              onClick={() => onChange({ ...value, towers: toggle(value.towers, t) })}
            >
              {t}
            </Chip>
          ))}
        </div>
      )}
      {value.mode === 'floors' && (
        <div className={styles.chips} role="group" aria-label="Andares">
          {floorsOf(units).map((f) => (
            <Chip
              key={f}
              selected={value.floors.includes(f)}
              onClick={() => onChange({ ...value, floors: toggle(value.floors, f) })}
            >
              Andar {f}
            </Chip>
          ))}
        </div>
      )}
      {value.mode === 'units' && (
        <div className={styles.chips} role="group" aria-label="Unidades">
          {units.map((u) => (
            <Chip
              key={u.id}
              selected={value.unitIds.includes(u.id)}
              onClick={() => onChange({ ...value, unitIds: toggle(value.unitIds, u.id) })}
            >
              {u.number} · {u.tower}
            </Chip>
          ))}
        </div>
      )}
      <span className={styles.hint}>{audienceLabel(value, units)}</span>
    </div>
  )
}
